/**
 * The address ledger and the shared-address report.
 *
 * Mirrors `apps/api/src/modules/crm-marketing/contacts.routes.ts` and the
 * shared half of `audience.routes.ts`. Read-only, all of it: there is no write
 * endpoint here on purpose, because suppressing an address and erasing one
 * already have doors with the guards that belong to them.
 *
 * ⚠ TWO WORDS THAT LOOK LIKE SYNONYMS AND ARE NOT.
 *
 *   A row in the LEDGER is an address a campaign ENROLLED. Enrolment sends
 *   nothing, so most of these have never been written to.
 *   `lastContactedAt` is what says a letter actually went out.
 *
 *   A row in the SHARED report is an address the REGISTER holds against several
 *   companies. Most of those are not in the ledger at all - they were screened
 *   out before a ledger row existed, which is the whole reason the report is
 *   worth looking at.
 */
import { api } from "./api";

/** The mailbox's own standing, which outlives every campaign. */
export type ContactStatus = "active" | "unsubscribed" | "bounced" | "complained" | "blocked";

export type ContactPersonType = "legal" | "natural" | "unknown";

export interface ContactRow {
  /** lower(trim), gmail dots dropped, plus-tags removed. The ledger's identity
   *  and NOT a sendable address - show `email`, query on this. */
  emailKey: string;
  /** The deliverable address, as the register spelled it. */
  email: string;
  status: ContactStatus;
  /**
   * Distinct register companies behind the address, as recorded at the last
   * enrolment. A stored number with one meaning - unlike `shared`, which is
   * this number compared against whichever campaign enrolled last.
   */
  companyCount: number;
  /** Letters that actually left. Zero for most rows. */
  contactCount: number;
  shared: boolean;
  freemail: boolean;
  personType: ContactPersonType;
  /** Where the address came from, which is what decides `personType`. */
  personSource: string;
  firstContactedAt: string | null;
  lastContactedAt: string | null;
  lastCampaignId: string | null;
  /** When the address entered the ledger. Enrolment, not contact. */
  createdAt: string;
}

export type ContactSort = "lastContacted" | "firstContacted" | "contacts" | "companies" | "address" | "added";

export interface ContactQuery {
  q?: string;
  status?: ContactStatus;
  personType?: ContactPersonType;
  /** "1" or "0" - a query string has no booleans, and "false" coerces to true. */
  freemail?: "1" | "0";
  contacted?: "1" | "0";
  minCompanies?: number;
  sort?: ContactSort;
  order?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface ContactPage {
  rows: ContactRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface ContactStats {
  /** Addresses in the ledger. Enrolled, not necessarily written to. */
  total: number;
  /** Addresses a letter has actually gone out to. */
  contacted: number;
  /** Out of every future audience: opted out, bounced, complained. */
  stopped: number;
  freemail: number;
  byStatus: Record<string, number>;
  byPersonType: Record<string, number>;
}

export interface SharedAddressRow {
  email: string;
  companies: number;
}

export interface SharedAddressPage {
  rows: SharedAddressRow[];
  /** ADDRESSES at or above the threshold. */
  total: number;
  /** COMPANIES behind them. A different unit; the screen has to say which. */
  companies: number;
  page: number;
  pageSize: number;
  pageCount: number;
  minCompanies: number;
  /** When the grouping was taken. It is held for five minutes. */
  computedAt: string;
}

export interface AddressCompany {
  regCode: string;
  name: string;
  /** "R" in the register, "L" in liquidation, "K" struck off. */
  status: string | null;
  webUrl: string | null;
}

export interface AddressLetter {
  id: string;
  campaignId: string;
  /** Null when the campaign was deleted; the ledger row survives it. */
  campaignName: string | null;
  status: string;
  skipReason: string | null;
  legalState: string | null;
  companyName: string | null;
  queuedAt: string;
  sentAt: string | null;
  /** Bounces, complaints and replies land here, days after `sentAt`. */
  eventAt: string | null;
  /** Set on retrying rows too, not only failed ones. */
  error: string | null;
}

export interface AddressDetail {
  address: string;
  emailKey: string;
  contact: {
    email: string;
    status: ContactStatus;
    companyCount: number;
    contactCount: number;
    shared: boolean;
    freemail: boolean;
    personType: ContactPersonType;
    personSource: string;
    firstContactedAt: string | null;
    lastContactedAt: string | null;
    createdAt: string;
  } | null;
  suppression: {
    source: string;
    reason: string | null;
    note: string | null;
    createdAt: string;
  } | null;
  companies: AddressCompany[];
  /** Exact, even when `companies` is capped. */
  companyTotal: number;
  letters: AddressLetter[];
  letterTotal: number;
}

const BASE = "/admin/crm/marketing";

function qs(params: Record<string, string | number | undefined>): string {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") out.set(k, String(v));
  }
  return out.toString();
}

export const listContacts = (params: ContactQuery = {}): Promise<ContactPage> =>
  api.get(`${BASE}/contacts?${qs(params as Record<string, string | number | undefined>)}`);

export const getContactStats = (): Promise<ContactStats> => api.get(`${BASE}/contacts/stats`);

/** One mailbox in full. Works for an address we have never seen: the answer is
 *  then empty lists rather than a 404, which is what the shared report is full
 *  of. */
export const getAddressDetail = (address: string): Promise<AddressDetail> =>
  api.get(`${BASE}/contacts/address?${qs({ address })}`);

export const listSharedAddressPage = (params: {
  minCompanies: number;
  page?: number;
  limit?: number;
}): Promise<SharedAddressPage> => api.get(`${BASE}/audience/shared?${qs(params)}`);
